import { useEffect, useRef } from 'react';
import type { Question, AnswerValue } from '../types';
import { ANSWER_OPTIONS } from '../data/questions';
import QuestionInfo from './QuestionInfo';

interface SectionQuestionListProps {
  questions: Question[];
  answers: Record<string, AnswerValue>;
  onAnswer: (questionId: string, value: AnswerValue) => void;
}

export default function SectionQuestionList({
  questions,
  answers,
  onAnswer,
}: SectionQuestionListProps) {
  const firstUnansweredRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to first unanswered question on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      firstUnansweredRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const firstUnansweredIndex = questions.findIndex((q) => !(q.id in answers));

  return (
    <div className="flex flex-col gap-3">
      {questions.map((q, idx) => {
        const currentAnswer = answers[q.id] ?? null;
        const isFirstUnanswered = idx === firstUnansweredIndex;

        return (
          <div
            key={q.id}
            ref={isFirstUnanswered ? firstUnansweredRef : undefined}
            className={`
              bg-purple-card rounded-xl border border-white/8 p-4
              ${!currentAnswer && !isFirstUnanswered ? 'opacity-70' : ''}
              ${isFirstUnanswered ? 'ring-1 ring-primary-500/30' : ''}
              transition-opacity duration-200
            `}
          >
            {/* Question text */}
            <p className="text-sm font-medium text-white mb-1">
              <span className="text-slate-400 mr-1.5">{idx + 1}.</span>
              {q.text}
            </p>
            {q.info && (
              <div className="mb-2">
                <QuestionInfo info={q.info} />
              </div>
            )}
            {!q.info && <div className="mb-2" />}

            {/* Emoji answer buttons */}
            <div className="flex gap-1.5 sm:gap-2">
              {ANSWER_OPTIONS.map((opt) => {
                const isSelected = currentAnswer === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => onAnswer(q.id, opt.value)}
                    className={`
                      w-9 h-9 sm:w-10 sm:h-10 rounded-lg text-base sm:text-lg
                      flex items-center justify-center
                      transition-all duration-150
                      ${
                        isSelected
                          ? 'bg-primary-500/20 ring-2 ring-primary-500 scale-110'
                          : 'bg-white/5 hover:bg-white/10 hover:scale-105'
                      }
                    `}
                    aria-label={opt.label}
                  >
                    {opt.emoji}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
